import { v4 as uuidv4 } from "uuid";
import { browserIsSafari, } from "../../shared/cookiecheck/cookie-check";
import { createLogger } from "../../utils";
import { resolveTargetingParams } from "./ad-targeting/targeting-params";
import getPlacementsForSection from "./placements";
import XandrAdService from "./xandrads";
var XandrAdEvent;
(function (XandrAdEvent) {
    XandrAdEvent["AdRequested"] = "adRequested";
    XandrAdEvent["AdAvailable"] = "adAvailable";
    XandrAdEvent["AdBadRequest"] = "adBadRequest";
    XandrAdEvent["AdLoaded"] = "adLoaded";
    XandrAdEvent["AdNoBid"] = "adNoBid";
    XandrAdEvent["AdRequestFailure"] = "adRequestFailure";
    XandrAdEvent["AdError"] = "adError";
    XandrAdEvent["AdCollapse"] = "adCollapse";
    XandrAdEvent["AdNoBidMediated"] = "adNoBidMediated";
})(XandrAdEvent || (XandrAdEvent = {}));
var XandrAdSource;
(function (XandrAdSource) {
    XandrAdSource[XandrAdSource["rtb"] = 0] = "rtb";
    XandrAdSource[XandrAdSource["csm"] = 1] = "csm";
})(XandrAdSource || (XandrAdSource = {}));
function isBannerAdObject(adObject) {
    return (typeof adObject.adType !== "undefined" &&
        adObject.adType === "banner");
}
function isVideoAdObject(adObject) {
    return (typeof adObject.adType !== "undefined" &&
        adObject.adType === "video");
}
function isNativeAdObject(adObject) {
    return (typeof adObject.adType !== "undefined" &&
        adObject.adType === "native");
}
const DEFAULT_ADS_GEO_TIMEOUT = 200;
class XandrAdRequest {
    constructor(context, config) {
        this.isFinished = false;
        this.astReady = false;
        this.readyTags = [];
        this.astResponsesReceived = 0;
        this.uuid = uuidv4();
        this.config = config;
        this.logger = createLogger(`XandrAdRequest - ${this.uuid}`);
        this.context = context;
        this.adSection =
            this.context.services.dataLayerService?.dataLayer.section ?? "";
        this.placements = getPlacementsForSection(config.placements, this.adSection);
        if (this.context.services.lazyloadService) {
            if (this.config.roadblocks) {
                this.roadblocks = this.config.roadblocks.filter((rb) => rb.sections.includes(this.adSection));
            }
        }
        this.keywords = {};
        this.device = {};
        this.user = {};
        this.adResponses = new Map();
        this.logger.logInfo(`Created new ad request ${this.uuid} for section ${this.adSection} with ${this.placements.length} placements`);
    }
    async resolveKeyValues() {
        this.logger.logInfo(`Resolving key-values for ad request`);
        this.context.core?.recordEvent("adreq.kvs.start", window.performance.now(), { request_uuid: this.uuid });
        this.keywords = await resolveTargetingParams(this.config.targeting, this.context, this.config.defaults.contextual?.relevancyLimits);
        // Add logged_in & accountType
        const dl = this.context.services.dataLayerService?.dataLayer;
        if (dl?.cdime || dl?.cdimu) {
            this.keywords = { ...this.keywords, logged_in: ["1"] };
        }
        if (dl?.accountType) {
            this.keywords = {
                ...this.keywords,
                accountType: [dl.accountType ?? ""],
            };
        }
        // Filter out empty keys
        this.keywords = Object.entries(this.keywords).reduce((acc, [key, value]) => {
            if ((value.length === 1 && typeof value[0] === "undefined") ||
                (value.length === 1 && value[0] === null) ||
                (value.length === 1 && value[0].length === 0) ||
                (Array.isArray(value) && value.length === 0)) {
                return acc;
            }
            return { ...acc, [key]: value };
        }, {});
        // // Set kvs as per tag now
        // this.placements.forEach((p) => {
        //   XandrAdService.modifyTagRequest(p.divId, {
        //     keywords: this.keywords,
        //   });
        // });
        this.context.core?.recordEvent("adreq.kvs.end", window.performance.now(), {
            request_uuid: this.uuid,
        });
        this.logger.logInfo(`Key-values resolved`);
    }
    async resolveDeviceObject() {
        this.logger.logInfo(`Resolving Xandr device object`);
        this.context.core?.recordEvent("adreq.device.start", window.performance.now(), { request_uuid: this.uuid });
        let deviceObj = {
            useragent: navigator.userAgent,
        };
        try {
            this.logger.logInfo(`Resolving Geo for device object`);
            let geoLoc;
            if (this.config.defaults.geoOptions?.enabled ?? false) {
                this.logger.logInfo(`Requesting geo`);
                this.context.core?.recordEvent("adreq.geo.start", window.performance.now());
                try {
                    geoLoc = await this.context.services.geoService?.getLocation(this.config.defaults.geoOptions?.timeout ?? DEFAULT_ADS_GEO_TIMEOUT, this.config.defaults.geoOptions?.acceptedCacheAge);
                }
                catch (e) {
                    this.logger.logError(`Error from geo: ${e}`);
                }
                this.context.core?.recordEvent("adreq.geo.end", window.performance.now());
            }
            else {
                this.logger.logInfo(`Geo disabled from ad config`);
            }
            this.logger.logInfo(`Geo object: ${JSON.stringify(geoLoc, null, 2)}`);
            const approx = geoLoc?.approximation?.country !== "abroad"
                ? {
                    city: geoLoc?.approximation?.city,
                    country: geoLoc?.approximation?.country,
                    region: geoLoc?.approximation?.region,
                    zip: geoLoc?.approximation?.postalCode,
                }
                : undefined;
            // Include coords even if we are abroad
            deviceObj = {
                ...deviceObj,
                geo: {
                    ...approx,
                    lat: geoLoc?.coordinates?.latitude,
                    lng: geoLoc?.coordinates?.longitude,
                },
                geoOverride: geoLoc?.approximation?.country !== "abroad"
                    ? {
                        countryCode: "FI",
                        zip: geoLoc?.approximation?.postalCode,
                    }
                    : undefined,
            };
            this.logger.logInfo(`Device object: ${JSON.stringify(deviceObj, null, 2)}`);
            // Delete hanging prop
            if (deviceObj.geo) {
                if (Object.entries(deviceObj.geo).filter(([, v]) => typeof v === "undefined").length === Object.keys(deviceObj.geo).length) {
                    delete deviceObj.geo;
                }
            }
            this.context.core?.recordEvent("adreq.device.end", window.performance.now(), { request_uuid: this.uuid });
            this.device = deviceObj;
        }
        catch (e) {
            this.logger.logError(`Device object failed: ${e}`);
        }
    }
    async resolveUserObject() {
        this.logger.logInfo(`Resolving Xandr user object`);
        this.context.core?.recordEvent("adreq.user.start", window.performance.now(), { request_uuid: this.uuid });
        this.logger.logInfo(`Checking if we should send externalUid`);
        let usePublisherId = false;
        if (typeof this.config.defaults.publisherId?.usePublisherId === "function") {
            this.logger.logInfo(`Publisher id check enabled`);
            try {
                usePublisherId = await this.config.defaults.publisherId?.usePublisherId(this.context);
            }
            catch (e) {
                this.logger.logInfo(`Publisher id check failed: ${e}`);
            }
        }
        else {
            this.logger.logInfo(`Default to checking if we have a Safari`);
            const isSafari = await browserIsSafari();
            if (typeof isSafari === "boolean") {
                this.logger.logInfo(`Browser is safari: ${isSafari}`);
                usePublisherId = isSafari;
            }
        }
        if (usePublisherId) {
            this.logger.logInfo(`We should send externalUid`);
            this.user = {
                ...this.user,
                externalUid: this.context.services.dataLayerService?.dataLayer.amid,
            };
        }
        else {
            this.logger.logInfo(`No need to send id`);
        }
        // Custom user object fields from config
        const customUserObj = Object.entries(this.config.user ?? {}).reduce((acc, [key, value]) => {
            try {
                if (typeof value === "function") {
                    return { ...acc, [key]: value(this.context) };
                }
                if (typeof value !== "undefined") {
                    return { ...acc, [key]: value };
                }
                return acc;
            }
            catch (e) {
                this.logger.logError(`Custom userObj failed: ${e}`);
            }
            return acc;
        }, {});
        this.logger.logDebug(`Custom userobj: ${JSON.stringify(customUserObj)}`);
        this.user = { ...this.user, ...customUserObj };
        this.context.core?.recordEvent("adreq.user.end", window.performance.now(), {
            request_uuid: this.uuid,
        });
        this.logger.logInfo(`Xandr user object resolved`);
    }
    async resolvePrebid() {
        if (this.context.services.prebidService) {
            // Setup ast early for Prebid
            this.setupAst();
            this.logger.logInfo(`We have Prebid, resolving...`);
            this.context.core?.recordEvent("adreq.prebid.start", window.performance.now(), { request_uuid: this.uuid });
            await this.context.services.prebidService.prebidPlacements(this.placements);
            this.logger.logInfo(`Prebid resolved!`);
            this.context.core?.recordEvent("adreq.prebid.end", window.performance.now(), { request_uuid: this.uuid });
        }
        else {
            this.logger.logInfo(`Prebid is not active`);
            return undefined;
        }
        return undefined;
    }
    updateAdResponse(resolve, event) {
        // Handle adAvailable
        if ("adType" in event) {
            this.adResponses.set(event.targetId, {
                status: XandrAdEvent.AdAvailable,
                adObject: event,
            });
        }
        // Handle adNobid
        if ("nobid" in event) {
            this.adResponses.set(event.targetId, {
                status: XandrAdEvent.AdNoBid,
            });
        }
        // Handle adBadRequest
        if ("errMessage" in event) {
            this.adResponses.set(event.targetId, {
                status: XandrAdEvent.AdBadRequest,
            });
        }
        this.logger.logDebug(`Ad unit ${event.targetId} ready`);
        // Handle having equal responses to requests
        this.astResponsesReceived += 1;
        if (this.astResponsesReceived ===
            Object.keys(window.apntag.requests.tags).length) {
            this.logger.logInfo(`All showTags have resolved`);
            this.isFinished = true;
            if (this.roadblocks) {
                this.context.services.lazyloadService?.setRoadblocks(this.roadblocks, this.adResponses);
            }
            if (this.readyTags) {
                this.readyTags.forEach((t) => {
                    this.setupTag(t);
                });
                this.readyTags.length = 0;
            }
            this.context.core?.recordEvent("adreq.v3.end", window.performance.now(), {
                request_uuid: this.uuid,
            });
            resolve();
        }
    }
    addAdEventListeners(resolve) {
        this.logger.logInfo("Adding listeners to ast.js ad events");
        this.placements.forEach((p) => {
            window.apntag.onEvent(XandrAdEvent.AdAvailable, p.divId, this.updateAdResponse.bind(this, resolve));
            window.apntag.onEvent(XandrAdEvent.AdNoBid, p.divId, this.updateAdResponse.bind(this, resolve));
            window.apntag.onEvent(XandrAdEvent.AdBadRequest, p.divId, this.updateAdResponse.bind(this, resolve));
        });
    }
    async requestAds() {
        return new Promise((resolve) => {
            this.setupAst();
            this.addAdEventListeners(resolve);
            this.installCustomNeeds(this.placements);
            this.logger.logInfo(`Firing ad request`);
            this.context.core?.recordEvent("adreq.v3.start", window.performance.now(), { request_uuid: this.uuid });
            XandrAdService.makeAdRequest();
        });
    }
    installCustomNeeds(placements) {
        this.logger.logInfo(`Installing custom hooks`);
        placements.forEach((p) => {
            const identifier = `${p.divId}/${p.placementId}/${p.friendlyName ?? "No friendly name"}`;
            // Always install timing event handlers
            window.apntag.onEvent(XandrAdEvent.AdAvailable, p.divId, () => {
                this.context.core?.recordEvent(`adreq.placement.${p.divId}.available`, window.performance.now(), { request_uuid: this.uuid });
            });
            window.apntag.onEvent(XandrAdEvent.AdLoaded, p.divId, () => {
                this.context.core?.recordEvent(`adreq.placement.${p.divId}.loaded`, window.performance.now(), { request_uuid: this.uuid });
            });
            // Always install timing event handler
            window.apntag.onEvent(XandrAdEvent.AdNoBid, p.divId, () => {
                this.context.core?.recordEvent(`adreq.placement.${p.divId}.noBid`, window.performance.now(), { request_uuid: this.uuid });
            });
            if (p.customNeeds) {
                this.logger.logDebug(`Placement ${identifier} has custom needs defined`);
                // adAvailable
                if (p.customNeeds.adAvailable &&
                    typeof p.customNeeds.adAvailable === "function") {
                    this.logger.logDebug(`Install adAvailable for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdAvailable, p.divId, (event) => {
                        try {
                            const e = event;
                            this.logger.logDebug(`Firing adAvailable for ${identifier}`);
                            p?.customNeeds?.adAvailable?.apply(window, [this.context, e]);
                        }
                        catch (error) {
                            this.logger.logError(`adAvailable for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adBadRequest
                if (p.customNeeds.adBadRequest &&
                    typeof p.customNeeds.adBadRequest === "function") {
                    this.logger.logDebug(`Install adBadRequest for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdBadRequest, p.divId, (event) => {
                        try {
                            const e = event;
                            this.logger.logDebug(`Firing adBadRequest for ${identifier}`);
                            p?.customNeeds?.adBadRequest?.apply(window, [this.context, e]);
                        }
                        catch (error) {
                            this.logger.logError(`adBadRequest for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adCollapse
                if (p.customNeeds.adCollapse &&
                    typeof p.customNeeds.adCollapse === "function") {
                    this.logger.logDebug(`Install adCollapse for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdCollapse, p.divId, (event) => {
                        try {
                            const e = event;
                            this.logger.logDebug(`Firing adCollapse for ${identifier}`);
                            p?.customNeeds?.adCollapse?.apply(window, [this.context, e]);
                        }
                        catch (error) {
                            this.logger.logError(`adCollapse for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adError
                if (p.customNeeds.adError &&
                    typeof p.customNeeds.adError === "function") {
                    this.logger.logDebug(`Install adError for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdError, p.divId, (arg0, arg1) => {
                        try {
                            this.logger.logDebug(`Firing adError for ${identifier}`);
                            p?.customNeeds?.adError?.apply(window, [
                                this.context,
                                arg0,
                                arg1,
                            ]);
                        }
                        catch (error) {
                            this.logger.logError(`adError for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adLoaded
                if (p.customNeeds.adLoaded &&
                    typeof p.customNeeds.adLoaded === "function") {
                    this.logger.logDebug(`Install adLoaded for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdLoaded, p.divId, (arg0) => {
                        try {
                            this.logger.logDebug(`Firing adLoaded for ${identifier}`);
                            p?.customNeeds?.adLoaded?.apply(window, [
                                this.context,
                                arg0,
                            ]);
                        }
                        catch (error) {
                            this.logger.logError(`adLoaded for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adNoBid
                if (p.customNeeds.adNoBid &&
                    typeof p.customNeeds.adNoBid === "function") {
                    this.logger.logDebug(`Install adNoBid for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdNoBid, p.divId, (arg0) => {
                        try {
                            this.logger.logDebug(`Firing adNoBid for ${identifier}`);
                            p?.customNeeds?.adNoBid?.apply(window, [
                                this.context,
                                arg0,
                            ]);
                        }
                        catch (error) {
                            this.logger.logError(`adNoBid for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adRequested
                if (p.customNeeds.adRequested &&
                    typeof p.customNeeds.adRequested === "function") {
                    this.logger.logDebug(`Install adRequested for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdRequested, p.divId, () => {
                        try {
                            this.logger.logDebug(`Firing adRequested for ${identifier}`);
                            p?.customNeeds?.adRequested?.apply(window, [this.context]);
                        }
                        catch (error) {
                            this.logger.logError(`adRequested for ${identifier} failed due to ${error}`);
                        }
                    });
                }
                // adRequestFailure
                if (p.customNeeds.adRequestFailure &&
                    typeof p.customNeeds.adRequestFailure === "function") {
                    this.logger.logDebug(`Install adRequestFailure for ${identifier}`);
                    window.apntag.onEvent(XandrAdEvent.AdRequestFailure, p.divId, (arg0) => {
                        try {
                            this.logger.logDebug(`Firing adRequestFailure for ${identifier}`);
                            p?.customNeeds?.adRequestFailure?.apply(window, [
                                this.context,
                                arg0,
                            ]);
                        }
                        catch (error) {
                            this.logger.logError(`adRequestFailure for ${identifier} failed due to ${error}`);
                        }
                    });
                }
            }
            else {
                this.logger.logDebug(`Placement ${p.divId}/${p.placementId}/${p.friendlyName} does not have custom needs defined`);
            }
        });
        this.logger.logInfo(`Custom hooks installed`);
    }
    setupTag(divId) {
        const placementObj = this.placements.find((p) => p.divId === divId);
        if (!placementObj) {
            this.logger.logWarn(`loadAd called for a placement not included in this request, ignoring`);
            return;
        }
        window.apntag.onEvent(XandrAdEvent.AdNoBid, divId, () => console.warn(`AD EVENT FOR ${divId}`));
        const renderAdCallback = async (arg0) => {
            const adObject = arg0;
            this.logger.logInfo(`adLoaded for ${divId} - type: ${adObject.adType}}`);
            if (adObject &&
                isVideoAdObject(adObject) &&
                this.config.outstream?.postResponse) {
                this.logger.logInfo(`Received video ad response`);
                if (this.config.outstream?.postResponse) {
                    this.logger.logInfo(`Calling postResponse hook for outstream`);
                    this.config.outstream?.postResponse(adObject, this.context);
                }
            }
            if (adObject && isNativeAdObject(adObject)) {
                this.logger.logInfo(`${divId} received a native response - render with template`);
                const nativeConf = this.placements.find((p) => p.divId === divId)?.native;
                if (nativeConf &&
                    (nativeConf.template || nativeConf.templateSelector)) {
                    let template = "";
                    if (typeof nativeConf.templateSelector === "function") {
                        template = nativeConf.templateSelector(this.context, adObject);
                    }
                    else if (typeof nativeConf.template === "string") {
                        template = nativeConf.template;
                    }
                    this.logger.logInfo(`Resolved template ${template} for ${divId}`);
                    await this.context.services.nativeAdsService?.renderNativeAd(adObject, template);
                }
            }
        };
        const renderAd = async () => {
            window.apntag.onEvent(XandrAdEvent.AdLoaded, divId, renderAdCallback);
            window.apntag.onEvent(XandrAdEvent.AdNoBid, divId, () => {
                this.logger.logInfo(`adNoBid - run collapse for mediation`);
                window.apntag.resizeAd(divId, [0, 0]);
            });
            // Add source name as data attribute
            const adDiv = window.document.getElementById(divId);
            if (adDiv) {
                const mediationSource = placementObj.mediation?.source ?? placementObj.prebid?.source;
                if (mediationSource) {
                    adDiv.dataset.mediationSource = mediationSource;
                }
            }
            window.apntag.showTag(divId);
        };
        if (this.context.services.lazyloadService &&
            !this.placements.find((p) => p.divId === divId)?.lazyload?.disable) {
            this.logger.logInfo(`loadAd called for ${divId} - lazyload is in control`);
            this.context.services.lazyloadService.lazyloadPlacement(divId, renderAd, this.adResponses.get(divId)?.adObject);
            this.context.services.lazyloadService.activateObserver(divId);
        }
        else {
            this.logger.logInfo(`loadAd called for ${divId} - lazyload disabled`);
            renderAd();
        }
    }
    setupAst() {
        if (this.astReady === false) {
            this.logger.logInfo(`Set pageopts for this request`);
            this.context.services.adsService?.setPageOpts({
                device: this.device,
                user: this.user,
                keywords: this.keywords,
            });
            // For LLD
            const externalImpId = [
                window?.ALMA?.dmp?.user?.()?.kuid,
                this.context.services.dataLayerService?.dataLayer.amid,
            ]
                .filter(Boolean)
                .join(":");
            // Setup placements
            if (this.context.services.adsService) {
                this.context.services.adsService.setupPlacements(this.placements, externalImpId);
            }
            this.astReady = true;
        }
    }
    loadAd(divId) {
        if (this.isFinished) {
            this.setupTag(divId);
        }
        else {
            this.readyTags.push(divId);
        }
    }
}
export { XandrAdRequest, XandrAdEvent, isBannerAdObject, isNativeAdObject, isVideoAdObject, };
//# sourceMappingURL=adrequest.js.map